<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { Head, Link, router, useForm, usePage } from "@inertiajs/vue3";
import { useClipboard } from "@vueuse/core";
import { useShareLink } from "@/composables/useShareLink";
import dayjs from "dayjs";
import { debounce } from "lodash";
import { useAuthUser } from "@/composables/useAuthUser";
import { useImageUpload } from "@/composables/useImageUpload";
import { useModalogStore } from "@/stores/modalog";
import { useToastStore } from "@/stores/toast.js";
import BrandButton from "@/Components/Brand/BrandButton.vue";
import CurrencySelect from "@/Components/forms/CurrencySelect.vue";
import DefaultCurrencyInlineModalog from "@/Components/Modalogs/DefaultCurrencyInlineModalog.vue";
import GiftCard from "@/Components/Profile/GiftCard.vue";
import ImageUpload from "@/Components/Actions/ImageUpload.vue";
import MoveItemMenu from "@/Components/Profile/MoveItemMenu.vue";
import PromoBanner from "@/Components/Profile/PromoBanner.vue";
import Regex from "@/utilities/regex";
import WishListDetailHero from "@/Components/Profile/WishListDetailHero.vue";

const modalogStore = useModalogStore();
const toastStore = useToastStore();
const authUser = useAuthUser();

// Access props through the page object so in-place reloads aren't read from
// a stale props reference.
const inertiaPage = usePage();

const owner = computed(() => inertiaPage.props.owner?.data);
const giftlist = computed(() => inertiaPage.props.giftlist?.data);
const item = computed(() => inertiaPage.props.item?.data);
const retailer = computed(() => item.value?.product?.retailer);
const moreItems = computed(() => inertiaPage.props.moreItems?.data ?? []);

const isLoggedIn = computed(() => inertiaPage.props.auth.user !== null);
const is_owner = computed(() => item.value?.is_owner);
const isExpertList = computed(() => giftlist.value?.type === "expert");

// --- Visibility gating (mirrors the legacy item detail rules) ---
const buyVisible = computed(() => !!item.value.url.length);

const shouldShowConfirmPurchase = computed(
    () => isLoggedIn.value && !isExpertList.value,
);

const confirmButtonVisible = computed(
    () =>
        item.value.qty_remaining > 0 &&
        isLoggedIn.value &&
        !owner.value.is_gift_hero,
);

const quantityVisible = computed(
    () => !owner.value.is_gift_hero && !isExpertList.value,
);

const itemsPurchased = computed(() => item.value.qty_purchased > 0);

const quantityReceived = computed(() =>
    Number(item.value.qty_desired - item.value.qty_remaining),
);

const itemsNeeded = computed(() => {
    // Owners and guests always see the number requested; purchase status is
    // only revealed to signed-in visitors.
    if (item.value.is_owner || !isLoggedIn.value) {
        return `${item.value.qty_desired} requested`;
    }

    if (quantityReceived.value === 0) {
        return `${item.value.qty_remaining} requested`;
    }

    if (!item.value.qty_remaining) {
        return "All have been purchased";
    }

    return `${quantityReceived.value} of ${item.value.qty_desired} purchased`;
});

const itemsPurchasedText = computed(() =>
    item.value.qty_purchased
        ? `You purchased ${item.value.qty_purchased} of this gift.`
        : itemsNeeded.value,
);

const retailerVisible = computed(() => retailer.value?.name !== "custom_item");

const sourceLabel = computed(() => {
    if (retailer.value?.hostname) {
        return retailer.value.hostname.replace(/^www\./, "");
    }

    if (retailerVisible.value && retailer.value?.name) {
        return retailer.value.name;
    }

    return null;
});

const priceLabel = computed(() => {
    const price = item.value.price > 0 ? item.value.price : 0;

    return `${item.value.price_symbol}${price}`;
});

const addedLabel = computed(
    () => `Added ${dayjs(item.value.created_at).format("M/D/YY")}`,
);

const disclaimerVisible = computed(
    () => retailerVisible.value && retailer.value?.hostname,
);

const noIndex = computed(
    () =>
        giftlist.value.privacy === "private" ||
        (!owner.value.is_gift_hero && !owner.value.is_organization),
);

// --- Buy / confirm / undo (same modalogs as the legacy page) ---
function handleBuyClick() {
    if (isExpertList.value) {
        window.open(item.value.url, "_blank", "noopener");
        return;
    }

    modalogStore.showModalog({
        type: "ModalogPreDeparture",
        props: {
            retailerName: retailerVisible.value ? retailer.value?.name : null,
            retailerUrl: item.value.url,
            item: item.value,
            showConfirmAfter:
                shouldShowConfirmPurchase.value &&
                item.value.qty_remaining > 0 &&
                !owner.value.is_gift_hero,
        },
    });
}

function showConfirmPurchase() {
    if (
        shouldShowConfirmPurchase.value &&
        item.value.qty_remaining > 0 &&
        !owner.value.is_gift_hero
    ) {
        modalogStore.showModalog({
            type: "ModalogConfirmPurchase",
            props: { item: item.value },
            isConfirmDisabled: false,
        });
    }
}

function showUndoPurchase() {
    modalogStore.showModalog({
        type: "ModalogConfirmPurchase",
        props: {
            isOwner: is_owner.value,
            item: item.value,
            isUnconfirm: true,
        },
    });
}

// --- Owner actions: copy / delete (move lives in MoveItemMenu) ---
const itemUrl = computed(() => route("item.detail", { item: item.value.uuid }));
const { copy, copied } = useClipboard({ source: itemUrl });
const { shareLink } = useShareLink();

// Tag the link so a signup through it is attributed back (GH-1192). Copy
// synchronously: shareLink() reports the token without awaiting.
function copyItemLink() {
    copy(shareLink(itemUrl.value, { type: "item", uuid: item.value.uuid }));
}

function deleteItem() {
    modalogStore.showModalog({
        type: "ModalogDeleteItem",
        props: { item: item.value },
    });
}

// Signed-in visitors can save gifts from expert lists to their own list.
function saveExpertItem() {
    modalogStore.showModalog({
        type: "ModalogMoveCopyItemToWishList",
        props: {
            isExpertItem: true,
            isOwner: false,
            item: item.value,
            listUuid: giftlist.value.uuid,
        },
        isConfirmDisabled: true,
    });
}

// --- Owner inline editing (name, price, quantity, notes) ---
const editing = ref(false);
const isValidPrice = ref(true);
const notesTextareaRef = ref(null);

const form = useForm({
    currency_code: item.value?.currency_code || "USD",
    list_uuid: giftlist.value?.uuid,
    name: item.value?.name || "",
    notes: item.value?.notes || "",
    price: item.value?.price || "",
    qty_desired: item.value?.qty_desired || 1,
});

/* Drop a field's message as soon as the user edits it, so it can't sit under a corrected value. */
["name", "price", "qty_desired"].forEach((field) => {
    watch(
        () => form[field],
        () => form.clearErrors(field),
    );
});

const isItemValid = computed(
    () =>
        (form.qty_desired > 0 && isValidPrice.value && form.name !== "") ||
        form.processing,
);

const validatePrice = debounce(function () {
    isValidPrice.value = String(form.price).search(Regex.priceInputs) >= 0;
}, 500);

function startEdit() {
    form.name = item.value.name || "";
    form.notes = item.value.notes || "";
    form.price = item.value.price || "";
    form.qty_desired = item.value.qty_desired || 1;
    form.currency_code = item.value.currency_code || "USD";
    form.clearErrors();
    editing.value = true;
    modalogStore.hasInlineModalog = false;
}

function cancelEdit() {
    editing.value = false;
    modalogStore.hasInlineModalog = false;
    form.clearErrors();
    isValidPrice.value = true;
}

function cancelEditEventHandler(event) {
    if (event.key === "Escape") {
        cancelEdit();
    }
}

function save() {
    form.put(route("item.update", { item: item.value.uuid }), {
        preserveScroll: true,
        onSuccess: () => {
            editing.value = false;
            router.reload({ only: ["item"] });
        },
    });
}

function eventCurrencyCodeSelected(currencyCode) {
    form.currency_code = currencyCode;
    modalogStore.hasInlineModalog = true;
}

function resizeTextarea() {
    notesTextareaRef.value.style.height = "auto";
    notesTextareaRef.value.style.height =
        notesTextareaRef.value.scrollHeight + 3 + "px";
}

onMounted(() => document.addEventListener("keyup", cancelEditEventHandler));
onUnmounted(() =>
    document.removeEventListener("keyup", cancelEditEventHandler),
);

// --- Owner image replacement (Cloudinary via the shared dropzone flow) ---
const {
    dropzoneRef,
    fetchUploadSignature,
    file,
    imageAdded,
    imageUploaded,
    uploadImage,
} = useImageUpload();

const imageEditing = ref(false);
const imageSaving = ref(false);

watch(imageEditing, (open) => {
    if (open) {
        fetchUploadSignature("custom_gift_photo");
    }
});

function updateItemImage() {
    imageSaving.value = true;

    uploadImage()
        .then((responseData) =>
            axios.post(
                route("item.update_photo", {
                    item: item.value.uuid,
                    photo_uuid: responseData.public_id,
                }),
            ),
        )
        .then(() => {
            toastStore.success("Your gift's photo was updated.");
            router.reload({ only: ["item"] });
        })
        .catch((error) => {
            console.error(error);
            toastStore.error("Something went wrong updating the photo.");
        })
        .finally(() => {
            imageSaving.value = false;
            imageEditing.value = false;
        });
}

// Icon vectors lifted from the Figma frame (224:2486)
const PENCIL_PATH =
    "M8.55516 1.42698L9.56583 0.416293C9.69753 0.28433 9.85395 0.179635 10.0262 0.108202C10.1984 0.0367691 10.383 0 10.5694 0C10.7558 0 10.9404 0.0367691 11.1126 0.108202C11.2848 0.179635 11.4413 0.28433 11.573 0.416293L12.5836 1.42698C13.1388 1.98214 13.1388 2.87894 12.5836 3.4341L11.573 4.44479L8.55516 1.42698ZM7.54448 2.43766L0 9.98219V13H3.01779L10.5623 5.45547L7.54448 2.43766Z";
const COPY_PATH =
    "M9.88235 0H3.95294C3.22824 0 2.63529 0.592941 2.63529 1.31765V9.22353C2.63529 9.94824 3.22824 10.5412 3.95294 10.5412H9.88235C10.6071 10.5412 11.2 9.94824 11.2 9.22353V1.31765C11.2 0.592941 10.6071 0 9.88235 0ZM9.88235 9.22353H3.95294V1.31765H9.88235V9.22353ZM0 8.56471V7.24706H1.31765V8.56471H0ZM0 4.94118H1.31765V6.25882H0V4.94118ZM4.61176 11.8588H5.92941V13.1765H4.61176V11.8588ZM0 10.8706V9.55294H1.31765V10.8706H0ZM1.31765 13.1765C0.592941 13.1765 0 12.5835 0 11.8588H1.31765V13.1765ZM3.62353 13.1765H2.30588V11.8588H3.62353V13.1765ZM6.91765 13.1765V11.8588H8.23529C8.23529 12.5835 7.64235 13.1765 6.91765 13.1765ZM1.31765 2.63529V3.95294H0C0 3.22824 0.592941 2.63529 1.31765 2.63529Z";

const inputClasses =
    "h-11 w-full rounded-[5px] border border-solid border-[#d0cac8] bg-white px-4 font-brand text-[15px] text-brand-navy focus:border-brand-navy focus:outline-none";

const railLinkClasses =
    "group inline-flex items-center gap-2.5 font-brand text-[15px] font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red";

const mobileActionClasses =
    "inline-flex items-center gap-1.5 rounded-[4px] px-1.5 py-1.5 font-brand text-[13px] font-semibold text-[#565a77] transition hover:bg-brand-cream hover:text-brand-navy";
</script>

<template>
    <div>
        <Head :title="item.name">
            <meta
                name="description"
                :content="`${item.name} from ${retailer?.name}`"
            />
            <meta
                property="og:title"
                :content="`${item.name} on ${owner.presentation_name}'s Gift Hero wish list`"
            />
            <meta
                property="og:description"
                content="Give and get the perfect gift. Every. Time."
            />
            <meta property="og:url" :content="item.open_graph.url" />
            <meta property="og:image" :content="item.open_graph.share_image" />
            <link rel="canonical" :href="item.open_graph.url" />
            <meta v-if="noIndex" name="robots" content="noindex, follow" />
        </Head>

        <WishListDetailHero
            :owner="owner"
            :is-owner="is_owner"
            :theme="owner.profile_theme"
            :back-href="giftlist.url"
            back-label="Back to Wish List"
            mobile-back-label="Back"
            round-bottom
        >
            <!-- Desktop rail: back link + owner item actions, left-aligned -->
            <template #rail>
                <div class="w-full max-lg:hidden">
                    <!-- Left-aligned to the same edge as the rail menu below.
                         Owner view: the strip's flow position starts under the
                         owner name, 27px above the gradient band's bottom
                         edge, so the top padding is 32px + 27px to center the
                         link between the band and the divider. (The name never
                         wraps — it truncates — so the offset is stable.)
                         Visitor view: the hero's Follow button sits between
                         the band and this strip, so a plain 32px gap works. -->
                    <div
                        class="flex items-center px-[35px] pb-8"
                        :class="is_owner ? 'pt-[59px]' : 'pt-8'"
                    >
                        <Link
                            :href="giftlist.url"
                            class="inline-flex items-center gap-3.5 font-brand text-[15px] font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red"
                        >
                            <!-- Red rounded triangle per Figma (deliberately
                                 breaks the currentColor pattern) -->
                            <svg
                                viewBox="0 -0.2 8 11.6"
                                class="h-2 w-[6px] text-brand-red"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path
                                    d="M0.731 6.79C0 6.21 0 5.1 0.731 4.52L5.649 0.615C6.599 -0.139 8 0.537 8 1.751L8 9.559C8 10.772 6.599 11.449 5.649 10.694Z"
                                />
                            </svg>
                            Back to Wish List
                        </Link>
                    </div>
                    <ul
                        v-if="is_owner"
                        class="flex flex-col items-start gap-6 border-y border-solid border-black/10 px-[35px] py-7"
                    >
                        <li>
                            <button
                                type="button"
                                :class="railLinkClasses"
                                @click="startEdit"
                            >
                                <svg
                                    viewBox="0 0 13 13"
                                    class="size-[13px] text-[#88959E] transition group-hover:text-brand-red"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path :d="PENCIL_PATH" />
                                </svg>
                                Edit Details
                            </button>
                        </li>
                        <li>
                            <MoveItemMenu
                                :item="item"
                                :list="giftlist"
                                align="left"
                                :trigger-class="railLinkClasses"
                                icon-class="h-[11px] w-[14px] text-[#88959E] transition group-hover:text-brand-red"
                            />
                        </li>
                        <li>
                            <button
                                type="button"
                                :class="railLinkClasses"
                                @click="copyItemLink()"
                            >
                                <svg
                                    viewBox="0 0 12 14"
                                    class="h-[14px] w-3 text-[#88959E] transition group-hover:text-brand-red"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path :d="COPY_PATH" />
                                </svg>
                                {{ copied ? "Link Copied!" : "Copy Link" }}
                            </button>
                        </li>
                        <li>
                            <button
                                type="button"
                                :class="railLinkClasses"
                                class="hover:!text-brand-red"
                                @click="deleteItem"
                            >
                                <svg
                                    viewBox="0 0 13 14"
                                    class="h-[14px] w-[13px] text-[#88959E] transition group-hover:text-brand-red"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="1.12"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M10.3805 2.61863V9.61381C10.3805 10.7661 10.3805 11.3422 10.1512 11.7823C9.94947 12.1695 9.6276 12.4842 9.23167 12.6814C8.78151 12.9057 8.19235 12.9057 7.01387 12.9057H5.33054C4.1521 12.9057 3.56287 12.9057 3.11277 12.6814C2.71684 12.4842 2.39495 12.1695 2.19321 11.7823C1.96387 11.3422 1.96387 10.7661 1.96387 9.61381V2.61863M0.561096 2.61863H11.7833M8.97776 2.61863L8.78797 2.0618C8.60399 1.5222 8.51197 1.2524 8.34139 1.05292C8.19073 0.876777 7.99729 0.740432 7.77874 0.656394C7.53122 0.561218 7.24042 0.561218 6.65869 0.561218H5.68572C5.10399 0.561218 4.8132 0.561218 4.56568 0.656394C4.34714 0.740432 4.15367 0.876777 4.00302 1.05292C3.83241 1.2524 3.74044 1.5222 3.55648 2.0618L3.36665 2.61863"
                                    />
                                </svg>
                                Delete
                            </button>
                        </li>
                    </ul>
                    <!-- Visitor rail: just Copy Link (BH-50) -->
                    <ul
                        v-else
                        class="flex flex-col items-start gap-6 border-y border-solid border-black/10 px-[35px] py-7"
                    >
                        <li>
                            <button
                                type="button"
                                :class="railLinkClasses"
                                data-test="copy-item-link"
                                @click="copyItemLink()"
                            >
                                <svg
                                    viewBox="0 0 12 14"
                                    class="h-[14px] w-3 text-[#88959E] transition group-hover:text-brand-red"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path :d="COPY_PATH" />
                                </svg>
                                {{ copied ? "Link Copied!" : "Copy Link" }}
                            </button>
                        </li>
                    </ul>
                </div>
            </template>

            <!-- Mobile: product image inside the gradient band, white behind
                 its lower half so it reads as bridging band and panel -->
            <template #mobile-band>
                <div class="relative px-2 pb-2">
                    <template v-if="!imageEditing">
                        <img
                            :src="item.image_url.original"
                            :key="item.image_url.original"
                            :alt="item.name"
                            class="aspect-[360/247] w-full rounded-[5px] bg-white object-cover"
                        />
                        <button
                            v-if="is_owner"
                            type="button"
                            class="absolute bottom-[15px] right-[15px] grid size-10 place-items-center rounded-[5px] bg-black/50 text-white transition hover:bg-black/70"
                            aria-label="Change image"
                            @click="imageEditing = true"
                        >
                            <svg
                                viewBox="0 0 13 13"
                                class="size-4"
                                fill="currentColor"
                                aria-hidden="true"
                            >
                                <path :d="PENCIL_PATH" />
                            </svg>
                        </button>
                    </template>
                    <div v-else class="relative rounded-[5px] bg-white p-3">
                        <ImageUpload
                            ref="dropzoneRef"
                            @image-added="imageAdded"
                            @image-uploaded="imageUploaded"
                        />
                        <div class="mt-3 flex items-center justify-end gap-3">
                            <button
                                type="button"
                                class="h-11 rounded-brand px-5 font-brand text-[15px] font-semibold text-[#565a77] transition hover:text-brand-navy disabled:pointer-events-none disabled:opacity-40"
                                :disabled="imageSaving"
                                @click.stop="imageEditing = false"
                            >
                                Cancel
                            </button>
                            <BrandButton
                                class="h-11 px-6 text-[15px] disabled:pointer-events-none disabled:opacity-40"
                                :disabled="!file"
                                :loading="imageSaving"
                                @click.stop="updateItemImage"
                            >
                                {{ imageSaving ? "Saving" : "Save" }}
                            </BrandButton>
                        </div>
                    </div>
                </div>
            </template>

            <div class="px-4 pb-10 pt-4 max-lg:pt-0 lg:px-[44px] lg:pb-[44px]">
                <!-- Desktop: back to list + added date -->
                <div
                    class="items-center justify-between pb-4 pt-2 max-lg:hidden lg:flex"
                >
                    <Link
                        :href="giftlist.url"
                        class="inline-flex items-center gap-2.5 font-brand text-[15px] font-semibold text-brand-navy underline underline-offset-2 transition hover:text-brand-red"
                    >
                        <svg
                            viewBox="0 -0.2 8 11.6"
                            class="h-2 w-[6px] text-brand-red"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                d="M0.731 6.79C0 6.21 0 5.1 0.731 4.52L5.649 0.615C6.599 -0.139 8 0.537 8 1.751L8 9.559C8 10.772 6.599 11.449 5.649 10.694Z"
                            />
                        </svg>
                        Back to ‘{{ giftlist.name }}’
                    </Link>
                    <p class="font-brand text-[13px] text-[#102a3d]">
                        Added
                        <i>{{ dayjs(item.created_at).format("M/D/YY") }}</i>
                    </p>
                </div>

                <!-- Product card -->
                <article
                    class="rounded-[4px] bg-white max-lg:-mx-4 max-lg:rounded-b-[10px] max-lg:rounded-t-none lg:grid lg:grid-cols-[1fr_1.1fr] lg:overflow-hidden"
                >
                    <!-- Desktop image cell (mobile shows it in the band). The
                         photo fills the cell edge-to-edge (cover crop, per the
                         mock); the card's height comes from the details column,
                         with a floor so short content can't squash the photo. -->
                    <div class="relative min-h-[436px] max-lg:hidden">
                        <template v-if="!imageEditing">
                            <img
                                :src="item.image_url.original"
                                :key="item.image_url.original"
                                :alt="item.name"
                                class="absolute inset-0 size-full rounded-l-[4px] object-cover"
                            />
                            <button
                                v-if="is_owner"
                                type="button"
                                class="absolute bottom-4 right-4 grid size-10 place-items-center rounded-[5px] bg-black/50 text-white transition hover:bg-black/70"
                                aria-label="Change image"
                                @click="imageEditing = true"
                            >
                                <svg
                                    viewBox="0 0 13 13"
                                    class="size-4"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path :d="PENCIL_PATH" />
                                </svg>
                            </button>
                        </template>
                        <!-- Editor fills the image cell: the dropzone grows,
                             the actions row keeps its height at the bottom. -->
                        <div v-else class="flex h-full flex-col p-5">
                            <ImageUpload
                                ref="dropzoneRef"
                                class="min-h-0 flex-1"
                                @image-added="imageAdded"
                                @image-uploaded="imageUploaded"
                            />
                            <div
                                class="mt-3 flex items-center justify-end gap-3"
                            >
                                <button
                                    type="button"
                                    class="h-11 rounded-brand px-5 font-brand text-[15px] font-semibold text-[#565a77] transition hover:text-brand-navy disabled:pointer-events-none disabled:opacity-40"
                                    :disabled="imageSaving"
                                    @click.stop="imageEditing = false"
                                >
                                    Cancel
                                </button>
                                <BrandButton
                                    class="h-11 px-6 text-[15px] disabled:pointer-events-none disabled:opacity-40"
                                    :disabled="!file"
                                    :loading="imageSaving"
                                    @click.stop="updateItemImage"
                                >
                                    {{ imageSaving ? "Saving" : "Save" }}
                                </BrandButton>
                            </div>
                        </div>
                    </div>

                    <!-- Details column -->
                    <div class="px-5 py-6 max-lg:pb-4 lg:px-12 lg:py-11">
                        <!-- Mobile meta row -->
                        <div
                            class="flex items-center justify-between pb-3 font-brand text-[13px] text-[#102a3d] lg:hidden"
                        >
                            <span>{{ addedLabel }}</span>
                            <span v-if="quantityVisible" class="lg:italic">{{
                                itemsNeeded
                            }}</span>
                        </div>

                        <!-- Read state -->
                        <template v-if="!editing">
                            <h1
                                class="item-detail__name font-brand text-[25px] font-bold leading-[1.25] text-[#102a3d]"
                            >
                                {{ item.name }}
                            </h1>

                            <!-- Gift details note (speech bubble) -->
                            <div
                                v-if="item.notes || is_owner"
                                class="mt-5 flex items-start gap-3"
                            >
                                <img
                                    v-if="owner.avatar?.img_85px_1x"
                                    :src="owner.avatar.img_85px_1x"
                                    :alt="owner.presentation_name"
                                    class="size-10 shrink-0 rounded-full object-cover"
                                />
                                <div
                                    class="relative flex-1 rounded-[6px] bg-[#f0eff2] px-4 py-3 max-lg:rounded-[10px] max-lg:bg-[#ececec]/80 max-lg:shadow-[inset_-1px_-1px_0.5px_#DCDCDC]"
                                >
                                    <!-- Mobile notch: a flap flush with the top
                                         edge, apex pointing at the avatar (per
                                         Figma 224-1097) -->
                                    <svg
                                        viewBox="0 0 27 12"
                                        class="absolute -left-[13px] top-0 h-3 w-[27px] lg:hidden"
                                        fill="#f0f0f0"
                                        aria-hidden="true"
                                    >
                                        <path d="M0 0 H27 V12 H14 Z" />
                                    </svg>
                                    <span
                                        class="absolute -left-1.5 top-4 size-3 rotate-45 bg-[#f0eff2] max-lg:hidden"
                                        aria-hidden="true"
                                    ></span>
                                    <p
                                        class="relative pr-6 font-brand text-[13px] leading-[1.35] text-black"
                                    >
                                        <template v-if="item.notes"
                                            >“{{ item.notes }}”</template
                                        >
                                        <span v-else class="text-black/70"
                                            >Add gift details — size, color,
                                            anything helpful.</span
                                        >
                                    </p>
                                    <button
                                        v-if="is_owner"
                                        type="button"
                                        class="absolute bottom-2 right-2 grid size-6 place-items-center rounded text-[#565a77] transition hover:text-brand-navy"
                                        aria-label="Edit gift details"
                                        @click="startEdit"
                                    >
                                        <svg
                                            viewBox="0 0 13 13"
                                            class="size-3.5"
                                            fill="currentColor"
                                            aria-hidden="true"
                                        >
                                            <path :d="PENCIL_PATH" />
                                        </svg>
                                    </button>
                                </div>
                            </div>

                            <!-- Price / retailer + actions -->
                            <div
                                class="mt-6 flex flex-col gap-5 lg:flex-row lg:justify-between"
                            >
                                <!-- lg: the wrapper dissolves (contents) so
                                     price / retailer / requested distribute
                                     evenly down the column -->
                                <div
                                    class="min-w-0 lg:flex lg:flex-col lg:justify-between lg:gap-4"
                                >
                                    <div
                                        class="flex items-baseline justify-between gap-4 lg:contents"
                                    >
                                        <p
                                            class="font-brand text-[18px] font-bold text-[#102a3d] lg:text-[20px]"
                                        >
                                            {{ priceLabel }}
                                            <span
                                                v-if="
                                                    item.currency_code !== 'USD'
                                                "
                                                class="text-[12px] font-normal text-[#565a77]"
                                                >{{ item.currency_code }}</span
                                            >
                                            <span
                                                v-if="item.qty_desired > 1"
                                                class="text-[13px] font-normal text-[#565a77]"
                                                >each</span
                                            >
                                        </p>
                                        <a
                                            v-if="sourceLabel && buyVisible"
                                            :href="item.original_url"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            class="truncate font-brand text-[13px] text-[#102a3d] hover:underline lg:block"
                                        >
                                            {{ sourceLabel }}
                                        </a>
                                    </div>
                                    <p
                                        v-if="quantityVisible"
                                        class="mt-4 font-brand text-[13px] italic text-[#102a3d] opacity-60 max-lg:hidden lg:mt-0"
                                    >
                                        {{ itemsNeeded }}
                                    </p>
                                </div>

                                <div
                                    class="flex w-full shrink-0 flex-col gap-3.5 lg:w-[220px]"
                                >
                                    <BrandButton
                                        v-if="buyVisible"
                                        class="gtm-item-buy h-[52px] w-full text-[15px] max-lg:h-[44px]"
                                        :data-hostname="retailer?.hostname"
                                        @click="handleBuyClick"
                                    >
                                        Buy Now
                                    </BrandButton>

                                    <template v-if="shouldShowConfirmPurchase">
                                        <button
                                            v-if="
                                                confirmButtonVisible &&
                                                !itemsPurchased
                                            "
                                            type="button"
                                            class="h-[52px] rounded-brand border border-solid border-[#eb3700] bg-transparent font-brand text-[15px] font-bold text-[#eb3700] transition hover:bg-[#eb3700]/5 max-lg:h-[44px]"
                                            @click="showConfirmPurchase"
                                        >
                                            Confirm as Purchased
                                        </button>
                                        <p
                                            v-if="itemsPurchased"
                                            class="flex items-center justify-between gap-3 font-brand text-[13px] text-[#102a3d]"
                                        >
                                            {{ itemsPurchasedText }}
                                            <button
                                                v-if="item.qty_purchased"
                                                type="button"
                                                class="font-semibold underline transition hover:text-brand-red"
                                                @click="showUndoPurchase"
                                            >
                                                Change
                                            </button>
                                        </p>
                                    </template>

                                    <button
                                        v-if="isExpertList && isLoggedIn"
                                        type="button"
                                        class="h-[52px] rounded-brand border border-solid border-brand-navy bg-transparent font-brand text-[15px] font-bold text-brand-navy transition hover:bg-brand-navy/5 max-lg:h-[44px]"
                                        @click="saveExpertItem"
                                    >
                                        Save to My Wish List
                                    </button>
                                </div>
                            </div>

                            <!-- Mobile owner actions -->
                            <div
                                v-if="is_owner"
                                class="-mx-5 mt-6 flex items-center justify-between border-t border-solid border-black/10 px-5 pt-4 lg:hidden"
                            >
                                <button
                                    type="button"
                                    :class="mobileActionClasses"
                                    @click="startEdit"
                                >
                                    <svg
                                        viewBox="0 0 13 13"
                                        class="size-3.5"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path :d="PENCIL_PATH" />
                                    </svg>
                                    Edit
                                </button>
                                <MoveItemMenu
                                    :item="item"
                                    :list="giftlist"
                                    align="left"
                                    :trigger-class="mobileActionClasses"
                                />
                                <button
                                    type="button"
                                    :class="mobileActionClasses"
                                    @click="copyItemLink()"
                                >
                                    <svg
                                        viewBox="0 0 16 18"
                                        class="h-[13px] w-[11px]"
                                        fill="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M13.5 0H5.4C4.41 0 3.6 0.81 3.6 1.8V12.6C3.6 13.59 4.41 14.4 5.4 14.4H13.5C14.49 14.4 15.3 13.59 15.3 12.6V1.8C15.3 0.81 14.49 0 13.5 0ZM13.5 12.6H5.4V1.8H13.5V12.6ZM0 11.7V9.9H1.8V11.7H0ZM0 6.75H1.8V8.55H0V6.75ZM6.3 16.2H8.1V18H6.3V16.2ZM0 14.85V13.05H1.8V14.85H0ZM1.8 18C0.81 18 0 17.19 0 16.2H1.8V18ZM4.95 18H3.15V16.2H4.95V18ZM9.45 18V16.2H11.25C11.25 17.19 10.44 18 9.45 18ZM1.8 3.6V5.4H0C0 4.41 0.81 3.6 1.8 3.6Z"
                                        />
                                    </svg>
                                    {{ copied ? "Copied!" : "Copy Link" }}
                                </button>
                                <button
                                    type="button"
                                    class="grid size-8 place-items-center rounded-[4px] text-[#565a77] transition hover:bg-brand-cream hover:text-brand-red"
                                    aria-label="Delete gift"
                                    @click="deleteItem"
                                >
                                    <svg
                                        viewBox="0 0 13 14"
                                        class="h-3.5 w-[13px]"
                                        fill="none"
                                        stroke="currentColor"
                                        stroke-width="1.12"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M10.3805 2.61863V9.61381C10.3805 10.7661 10.3805 11.3422 10.1512 11.7823C9.94947 12.1695 9.6276 12.4842 9.23167 12.6814C8.78151 12.9057 8.19235 12.9057 7.01387 12.9057H5.33054C4.1521 12.9057 3.56287 12.9057 3.11277 12.6814C2.71684 12.4842 2.39495 12.1695 2.19321 11.7823C1.96387 11.3422 1.96387 10.7661 1.96387 9.61381V2.61863M0.561096 2.61863H11.7833M8.97776 2.61863L8.78797 2.0618C8.60399 1.5222 8.51197 1.2524 8.34139 1.05292C8.19073 0.876777 7.99729 0.740432 7.77874 0.656394C7.53122 0.561218 7.24042 0.561218 6.65869 0.561218H5.68572C5.10399 0.561218 4.8132 0.561218 4.56568 0.656394C4.34714 0.740432 4.15367 0.876777 4.00302 1.05292C3.83241 1.2524 3.74044 1.5222 3.55648 2.0618L3.36665 2.61863"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <!-- Missing-info alert (owner) -->
                            <div
                                v-if="
                                    is_owner &&
                                    item.edit_requirements.length > 0 &&
                                    item.edit_status & ~4
                                "
                                class="mt-5 rounded-[4px] border border-solid border-yellow-300 bg-yellow-50 p-4 font-brand text-[13px] text-yellow-800"
                                role="alert"
                            >
                                <p class="font-bold">
                                    Use Edit Details to add the following:
                                </p>
                                <ul class="mt-1.5 list-inside list-disc">
                                    <li
                                        v-for="requirement in item.edit_requirements"
                                        :key="requirement"
                                    >
                                        {{ requirement }}
                                    </li>
                                </ul>
                            </div>

                            <!-- Price disclaimer (desktop: inside the card;
                                 mobile renders it on cream below, per Figma) -->
                            <p
                                v-if="disclaimerVisible"
                                class="mt-6 font-brand text-[10px] leading-[1.4] text-[#102a3d] opacity-70 max-lg:hidden"
                            >
                                * Product prices and availability are accurate
                                as of
                                {{
                                    dayjs(item.price_updated_at).format(
                                        "MMMM D, YYYY h:mm A",
                                    )
                                }}
                                ET and are subject to change. Any price and
                                availability information displayed on
                                {{ retailer.hostname }} at the time of purchase
                                will apply to the purchase of this product.
                            </p>
                        </template>

                        <!-- Edit state (owner) -->
                        <div v-else class="flex flex-col gap-4">
                            <div>
                                <label
                                    for="item-name"
                                    class="mb-1.5 block font-brand text-[13px] font-semibold text-brand-navy"
                                    >Name</label
                                >
                                <input
                                    id="item-name"
                                    v-model="form.name"
                                    type="text"
                                    name="item-name"
                                    autocomplete="off"
                                    required
                                    maxlength="255"
                                    :class="inputClasses"
                                />
                                <p
                                    v-if="form.errors.name"
                                    class="mt-1 font-brand text-[12px] text-brand-red"
                                >
                                    {{ form.errors.name }}
                                </p>
                            </div>

                            <div class="flex flex-wrap items-end gap-4">
                                <div class="w-[140px]">
                                    <label
                                        for="item-price"
                                        class="mb-1.5 block font-brand text-[13px] font-semibold text-brand-navy"
                                        >Price</label
                                    >
                                    <div class="relative">
                                        <span
                                            class="absolute left-3 top-1/2 -translate-y-1/2 font-brand text-[15px] text-[#565a77]"
                                            >{{ item.price_symbol }}</span
                                        >
                                        <input
                                            id="item-price"
                                            v-model="form.price"
                                            type="text"
                                            name="item-price"
                                            required
                                            pattern="[0-9]+(\.[0-9][0-9]?)?"
                                            autocomplete="off"
                                            :class="inputClasses"
                                            class="!pl-7"
                                            @input="validatePrice"
                                        />
                                    </div>
                                </div>
                                <CurrencySelect
                                    :currency-code="form.currency_code"
                                    @currency-code-selected="
                                        eventCurrencyCodeSelected
                                    "
                                />
                                <div v-if="!isExpertList" class="w-[110px]">
                                    <label
                                        for="item-quantity"
                                        class="mb-1.5 block font-brand text-[13px] font-semibold text-brand-navy"
                                        >Quantity</label
                                    >
                                    <input
                                        id="item-quantity"
                                        v-model="form.qty_desired"
                                        type="number"
                                        name="item-quantity"
                                        min="1"
                                        max="500"
                                        pattern="\d*"
                                        required
                                        autocomplete="off"
                                        :class="inputClasses"
                                    />
                                </div>
                            </div>
                            <p
                                v-if="!isValidPrice"
                                class="font-brand text-[12px] text-brand-red"
                            >
                                Price can only contain numbers and a single
                                period.
                            </p>
                            <p
                                v-if="form.errors.price"
                                class="font-brand text-[12px] text-brand-red"
                            >
                                {{ form.errors.price }}
                            </p>
                            <p
                                v-if="form.errors.qty_desired"
                                class="font-brand text-[12px] text-brand-red"
                            >
                                {{ form.errors.qty_desired }}
                            </p>

                            <DefaultCurrencyInlineModalog
                                v-show="modalogStore.hasInlineModalog"
                                :currency-code="form.currency_code"
                                :currency-symbol="
                                    $page.props.currencies[form.currency_code]
                                "
                            />

                            <div>
                                <label
                                    for="item-notes"
                                    class="mb-1.5 block font-brand text-[13px] font-semibold text-brand-navy"
                                    >Gift Details</label
                                >
                                <textarea
                                    id="item-notes"
                                    ref="notesTextareaRef"
                                    v-model="form.notes"
                                    name="item-comments"
                                    rows="2"
                                    placeholder="For example: 'the blue one' or 'size medium', etc."
                                    :class="inputClasses"
                                    class="h-auto min-h-[66px] py-3"
                                    @input="resizeTextarea"
                                ></textarea>
                            </div>

                            <div class="flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    class="h-11 rounded-brand px-5 font-brand text-[15px] font-semibold text-[#565a77] transition hover:text-brand-navy disabled:pointer-events-none disabled:opacity-40"
                                    :disabled="form.processing"
                                    @click="cancelEdit"
                                >
                                    Cancel
                                </button>
                                <BrandButton
                                    class="h-11 px-6 text-[15px] disabled:pointer-events-none disabled:opacity-40"
                                    :disabled="!isItemValid"
                                    :loading="form.processing"
                                    @click="save"
                                >
                                    {{ form.processing ? "Saving" : "Save" }}
                                </BrandButton>
                            </div>
                        </div>
                    </div>
                </article>

                <!-- Mobile price disclaimer, outside the white card -->
                <p
                    v-if="disclaimerVisible && !editing"
                    class="mt-4 font-brand text-[10px] leading-[1.4] text-[#102a3d] opacity-70 lg:hidden"
                >
                    * Product prices and availability are accurate as of
                    {{
                        dayjs(item.price_updated_at).format(
                            "MMMM D, YYYY h:mm A",
                        )
                    }}
                    ET and are subject to change. Any price and availability
                    information displayed on {{ retailer.hostname }} at the time
                    of purchase will apply to the purchase of this product.
                </p>

                <!-- More from this list -->
                <section v-if="moreItems.length" class="mt-10">
                    <h2
                        class="text-center font-brand text-[25px] font-bold text-[#102a3d]"
                    >
                        More from This List
                    </h2>
                    <div
                        class="mt-6 grid gap-6 max-lg:gap-[22px] sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                    >
                        <GiftCard
                            v-for="moreItem in moreItems"
                            :key="moreItem.uuid"
                            :item="moreItem"
                            :list="giftlist"
                            :is-owner="is_owner"
                        />
                    </div>
                </section>
            </div>
        </WishListDetailHero>

        <!-- Promo banners (mock shows them for every state; the gold CTA
             swaps to a sensible destination for signed-in users). No
             item-detail__* class here — legacy _item-detail.scss styles that
             block (gradient + border) and would repaint the wrapper. -->
        <div
            class="mt-6 grid gap-6 px-4 pb-10 max-lg:mt-0 max-lg:bg-brand-cream max-lg:pt-6 lg:grid-cols-2 lg:pl-[282px] lg:pr-[35px]"
        >
            <PromoBanner
                headline="The Ultimate
Gift Giver"
                :cta-label="
                    authUser ? 'Create a Wish List' : 'Create an Account'
                "
                :cta-href="authUser ? route('dashboard') : route('register')"
                image="images/profile/promo-gift-giver.png"
                variant="gold"
                compact
            />
            <PromoBanner
                headline="Need Some
Giftspiration?"
                cta-label="Explore Gift Ideas"
                :cta-href="route('explore')"
                image="images/profile/cta-collage-diagonal.png"
                mobile-image="images/profile/cta-collage-mobile.png"
                variant="purple"
                compact
                image-class="max-lg:-mt-2 max-lg:w-full lg:absolute lg:inset-y-0 lg:right-0 lg:h-full lg:w-auto lg:object-contain lg:object-right"
            />
        </div>
    </div>
</template>
